/**
 * History chart color palette.
 * Eventually users may upload their own; we supply a default.
 * Colors accept any valid CSS value (names, hex, rgb, etc.).
 */

import type React from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PaletteEntry {
  label: string
  color: string
}

export interface ZonePalette {
  supply: string
  return: string
  setpoint: string
  virtualSetPoint: string
  modes: Record<string, PaletteEntry>
}

export interface SpeedThreshold {
  /** Minimum speed (kph) for this color band */
  min: number
  color: string
}

export type LinePattern = 'solid' | 'dashed' | 'dotted' | number[]

export interface HistoryPalette {
  geofence: Record<string, string>
  powerState: Record<string, PaletteEntry>
  powerMode: Record<string, PaletteEntry>
  operation: Record<string, PaletteEntry>
  powerSource: Record<string, PaletteEntry>
  engineSpeed: Record<string, PaletteEntry>
  airControl: Record<string, PaletteEntry>
  zones: ZonePalette[]
  zoneLinePatterns: LinePattern[]
  monitoringPoints: string[]
  ambient: string
  door: Record<string, string>
  movement: SpeedThreshold[]
  alarms: Record<string, string>
  alerts: Record<string, string>
}

// ---------------------------------------------------------------------------
// Default palette
// ---------------------------------------------------------------------------

// The status bar colors match Refresh's history graph (refresh d3-time-line.js legendColors).
const SHARED_ZONE_MODES: Record<string, PaletteEntry> = {
  C: { label: 'Cooling', color: '#6464CA' },
  D: { label: 'Defrost', color: '#E6D682' },
  H: { label: 'Heating', color: '#DE5858' },
  N: { label: 'Null', color: '#388E8E' },
  O: { label: 'Active', color: '#FEB9FE' },
  P: { label: 'Pre-trip', color: 'purple' },
  S: { label: 'Sleep', color: 'brown' },
}

export const defaultPalette: HistoryPalette = {
  geofence: { in: 'gray', out: '#3b82f6' },

  powerState: {
    F: { label: 'Reefer Off', color: '#DE5858' },
    O: { label: 'Reefer On', color: '#4AA44A' },
  },
  powerMode: {
    A: { label: 'Active', color: '#6464CA' },
    S: { label: 'Sleeping', color: '#4AA44A' },
    I: { label: 'Inactive', color: '#9E9E9E' },
  },
  operation: {
    S: { label: 'Start/Stop', color: '#4AA44A' },
    C: { label: 'Continuous', color: '#CDAD00' },
    L: { label: 'Sleep', color: '#E6D682' },
  },
  powerSource: {
    D: { label: 'Diesel', color: '#9E9E9E' },
    E: { label: 'Electricity', color: '#FEB9FE' },
  },
  engineSpeed: {
    L: { label: 'Low', color: '#4AA44A' },
    H: { label: 'High', color: '#DE5858' },
  },
  airControl: {
    R: { label: 'Return Air', color: '#DE5858' },
    S: { label: 'Supply Air', color: '#6464CA' },
  },

  zones: [
    { supply: '#dc2626', return: '#2563eb', setpoint: '#7c3aed', virtualSetPoint: '#1e1b4b', modes: SHARED_ZONE_MODES },
    { supply: '#dc2626', return: '#2563eb', setpoint: '#7c3aed', virtualSetPoint: '#1e1b4b', modes: SHARED_ZONE_MODES },
    { supply: '#dc2626', return: '#2563eb', setpoint: '#7c3aed', virtualSetPoint: '#1e1b4b', modes: SHARED_ZONE_MODES },
  ],

  /** Line dash patterns per zone index — used on the "All" tab to differentiate zones */
  zoneLinePatterns: ['solid', 'dashed', 'dotted'] as LinePattern[],

  monitoringPoints: ['teal', 'maroon', '#f59e0b', '#ec4899', 'chocolate', 'darkkhaki', 'orange', 'springgreen'],

  ambient: '#ca8a04',

  door: { open: '#F18484', closed: 'blue' },

  movement: [
    { min: 0, color: 'blue' },
    { min: 25, color: 'yellow' },
    { min: 50, color: 'red' },
    { min: 90, color: '#1e293b' },
  ],

  alarms: { critical: '#DE5858', warning: '#FFFF00', informational: '#4AA44A' },
  alerts: { critical: '#F18484', warning: '#FFC75C', informational: '#4ADE80' },
}

// ---------------------------------------------------------------------------
// Palette section keys that use PaletteEntry (label + color)
// ---------------------------------------------------------------------------

const ENTRY_SECTION_KEYS = ['powerState', 'powerMode', 'operation', 'powerSource', 'engineSpeed', 'airControl'] as const

type EntrySectionKey = (typeof ENTRY_SECTION_KEYS)[number]

function isEntrySectionKey(key: string): key is EntrySectionKey {
  return (ENTRY_SECTION_KEYS as readonly string[]).includes(key)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FALLBACK = '#94a3b8'

const HATCH_SUFFIX = '-hatch'

/** Check if a color string uses a hatch pattern (e.g. "gray-hatch") */
export function isHatchColor(color: string): boolean {
  return color.endsWith(HATCH_SUFFIX)
}

/** Get the base color from a hatch color string */
function getHatchBaseColor(color: string): string {
  return color.slice(0, -HATCH_SUFFIX.length)
}

// 8×8 SVG tile with a diagonal stripe — used on the row container so the
// pattern is continuous across all hatch-colored cells.
const HATCH_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8'><path d='M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4' stroke='rgba(255,255,255,0.45)' stroke-width='2'/></svg>`
const HATCH_URL = `url("data:image/svg+xml,${encodeURIComponent(HATCH_SVG)}")`

/** CSS background for a row container that has hatch cells.
 *  Provide the base color so the hatch renders on the right background. */
export function hatchParentStyle(baseColor: string): React.CSSProperties {
  return {
    backgroundColor: baseColor,
    backgroundImage: HATCH_URL,
    backgroundRepeat: 'repeat',
  }
}

/** Build CSS background style for a single cell.
 *  When the row has a hatch parent, hatch cells should be transparent
 *  so the continuous parent pattern shows through. */
export function colorStyle(color: string, hasHatchParent = false): React.CSSProperties {
  if (!isHatchColor(color)) {
    return { backgroundColor: color }
  }
  if (hasHatchParent) {
    // Transparent — let the parent's pattern show through
    return { backgroundColor: 'transparent' }
  }
  // Standalone (e.g. legend swatch) — render inline
  return {
    backgroundColor: getHatchBaseColor(color),
    backgroundImage: HATCH_URL,
    backgroundRepeat: 'repeat',
  }
}
/** Movement and geofence-visit bars, as in Refresh. */
export const MOVEMENT_COLOR = '#4AA44A'

/** Interpolate green→yellow→red for speed gradient (0→100+ km/h) */
function resolveSpeedGradientColor(speedKph: number): string {
  const t = Math.min(speedKph / 100, 1)
  if (t <= 0.5) {
    // green → yellow
    const s = t * 2
    const r = Math.round(74 + (255 - 74) * s)
    const g = Math.round(222 + (199 - 222) * s)
    const b = Math.round(128 + (92 - 128) * s)
    return `rgb(${r},${g},${b})`
  }
  // yellow → red
  const s = (t - 0.5) * 2
  const r = Math.round(255 + (241 - 255) * s)
  const g = Math.round(199 + (132 - 199) * s)
  const b = Math.round(92 + (132 - 92) * s)
  return `rgb(${r},${g},${b})`
}

/** Resolve color for a given lane value */
export function resolveColor(
  palette: HistoryPalette,
  colorMapKey: string,
  value: string,
  numericValue?: number
): string {
  if (isEntrySectionKey(colorMapKey)) {
    return palette[colorMapKey][value]?.color ?? FALLBACK
  }
  if (colorMapKey === 'zoneMode') {
    return palette.zones[0]?.modes[value]?.color ?? FALLBACK
  }
  if (colorMapKey === 'door') return palette.door[value] ?? FALLBACK
  if (colorMapKey === 'alarm') return palette.alarms[value] ?? FALLBACK
  if (colorMapKey === 'alert') return palette.alerts[value] ?? FALLBACK
  if (colorMapKey === 'geofencePresence') return palette.geofence[value] ?? FALLBACK
  if (colorMapKey === 'speed') return resolveSpeedColor(palette, numericValue ?? 0)
  if (colorMapKey === 'movement') return MOVEMENT_COLOR
  if (colorMapKey === 'monitoringPoint') return palette.monitoringPoints[0] ?? FALLBACK
  if (colorMapKey === 'speedGradient') return resolveSpeedGradientColor(numericValue ?? 0)
  if (colorMapKey === 'ambient') return '#F5A623' // softer warm orange
  if (colorMapKey === 'setpoint') return '#4ADE80' // softer green
  if (colorMapKey === 'returnAir') return '#9CA3AF' // gray
  if (colorMapKey === 'supplyAir') return '#9CA3AF' // gray
  if (colorMapKey === 'tempGradient') return '#4ADE80' // fallback green, computed inline
  if (colorMapKey === 'geofenceColor') return value // value IS the color
  return FALLBACK
}

/** Resolve display label for a lane interval value */
export function resolveLabel(
  palette: HistoryPalette,
  colorMapKey: string,
  value: string,
  numericValue?: number
): string {
  if (isEntrySectionKey(colorMapKey)) {
    return palette[colorMapKey][value]?.label ?? value
  }
  if (colorMapKey === 'zoneMode') {
    return palette.zones[0]?.modes[value]?.label ?? value
  }
  if (colorMapKey === 'speed' && numericValue != null) {
    return `${numericValue.toFixed(1)} km/h`
  }
  if (colorMapKey === 'speedGradient' && numericValue != null) {
    return `${numericValue.toFixed(1)} km/h`
  }
  if (colorMapKey === 'monitoringPoint' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  if (colorMapKey === 'tempGradient' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  if (colorMapKey === 'ambient' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  if (colorMapKey === 'setpoint' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  if (colorMapKey === 'returnAir' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  if (colorMapKey === 'supplyAir' && numericValue != null) {
    return `${numericValue.toFixed(1)}\u00b0C`
  }
  // For simple string sections, capitalize the value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** Resolve speed color from movement thresholds */
export function resolveSpeedColor(palette: HistoryPalette, speedKph: number): string {
  const thresholds = palette.movement
  let color = thresholds[0]?.color ?? FALLBACK
  for (const t of thresholds) {
    if (speedKph >= t.min) color = t.color
    else break
  }
  return color
}

/** Get the top speed value (highest threshold min) for bar-height scaling */
export function getTopSpeed(palette: HistoryPalette): number {
  const thresholds = palette.movement
  return thresholds.length ? thresholds[thresholds.length - 1].min : 100
}

/** Build legend entries for a given colorMapKey */
export function getColorLegendEntries(
  palette: HistoryPalette,
  colorMapKey: string
): { key: string; label: string; color: string }[] {
  if (isEntrySectionKey(colorMapKey)) {
    return Object.entries(palette[colorMapKey]).map(([k, e]) => ({ key: k, label: e.label, color: e.color }))
  }
  if (colorMapKey === 'zoneMode') {
    const modes = palette.zones[0]?.modes ?? {}
    return Object.entries(modes).map(([k, e]) => ({ key: k, label: e.label, color: e.color }))
  }
  if (colorMapKey === 'door') {
    return Object.entries(palette.door).map(([k, c]) => ({ key: k, label: capitalize(k), color: c }))
  }
  if (colorMapKey === 'alarm') {
    return Object.entries(palette.alarms).map(([k, c]) => ({ key: k, label: capitalize(k), color: c }))
  }
  if (colorMapKey === 'alert') {
    return Object.entries(palette.alerts).map(([k, c]) => ({ key: k, label: capitalize(k), color: c }))
  }
  if (colorMapKey === 'geofencePresence') {
    return Object.entries(palette.geofence).map(([k, c]) => ({ key: k, label: capitalize(k), color: c }))
  }
  if (colorMapKey === 'speed') {
    return palette.movement.map((t) => ({ key: `${t.min}`, label: `\u2265${t.min} km/h`, color: t.color }))
  }
  if (colorMapKey === 'movement') {
    return [{ key: 'moving', label: 'Moving', color: MOVEMENT_COLOR }]
  }
  return []
}

/** Bar label text that stays readable on light fills (Refresh's yellows and pink). */
export function labelColorFor(fill: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(fill)?.[1]
  if (!hex) return '#fff'
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16))
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#1f2937' : '#fff'
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
